'use client';

import {usePathname, useSearchParams} from 'next/navigation';
import {Suspense, useEffect, useRef} from 'react';
import {logAnalyticsEvent, setAnalyticsConfig, type AnalyticsConfig} from './analytics';

function PageViewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  // Dedupes consecutive page_views for the SAME path only (React StrictMode
  // runs effects twice in dev, which would double-count page views). Other
  // analytics events never touch this ref, and navigating away and back
  // refires because the stored path differs in between.
  const lastTrackedPagePath = useRef<string | null>(null);

  useEffect(() => {
    const pagePath = search ? `${pathname}?${search}` : pathname;
    if (lastTrackedPagePath.current === pagePath) {
      return;
    }
    lastTrackedPagePath.current = pagePath;
    logAnalyticsEvent('page_view', {
      page_path: pagePath,
      // document.title may still be the previous page's title on SPA
      // navigations (Next.js applies metadata after this effect) — accepted
      // inaccuracy; GA4 reporting keys on page_path/page_location.
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, search]);

  return null;
}

export function AnalyticsPageViews({config}: {config: AnalyticsConfig | null}) {
  // Applied during render, not in an effect: other components call logAnalyticsEvent from their
  // own effects and handlers, and this component sits at the top of the server layout, so the
  // config must already be in place by the time any of those run.
  setAnalyticsConfig(config);

  return (
    <Suspense fallback={null}>
      <PageViewTracker />
    </Suspense>
  );
}
